<template>
  <div class="sdk-stats-page w-100">
    <Wrapper>
      <!-- Chưa cấu hình gateway -->
      <b-alert v-if="!isConfigured" show variant="warning" class="mb-0">
        Chưa cấu hình <code>VUE_APP_SDK_GATEWAY</code> trong file môi trường.
      </b-alert>

      <!-- Cần đăng nhập -->
      <div v-else-if="needLogin" class="sdk-login-panel">
        <h5 class="mb-4">Đăng nhập Gateway quản trị SDK</h5>
        <form @submit.prevent="doLogin">
          <b-row>
            <b-col :cols="12" :md="5">
              <b-form-group label="Email">
                <b-form-input v-model="loginForm.email" placeholder="Nhập email admin" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="5">
              <b-form-group label="Mật khẩu">
                <b-form-input v-model="loginForm.password" type="password" placeholder="Nhập mật khẩu" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="2" class="d-flex align-items-end">
              <b-button type="submit" class="btn btn-success mb-3 w-100" :disabled="loginLoading">
                <b-spinner v-if="loginLoading" small class="mr-1" />
                Đăng nhập
              </b-button>
            </b-col>
          </b-row>
        </form>
      </div>

      <template v-else>
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 class="mb-0">Thống kê sử dụng dịch vụ</h4>
          </div>
          <div class="d-flex gap-2">
            <b-button class="btn btn-light mr-2" @click="$router.push({ name: 'sdk_partner' })">
              <i class="fas fa-arrow-left mr-1" />Quay lại
            </b-button>
            <b-button class="btn btn-outline-secondary mr-2" :disabled="loading" @click="loadStats">
              <b-spinner v-if="loading" small class="mr-1" />
              <i v-else class="fas fa-sync-alt mr-1" />Làm mới
            </b-button>
            <b-button class="btn btn-outline-success" @click="exportCsv">
              <i class="fas fa-file-csv mr-1" />Xuất CSV
            </b-button>
          </div>
        </div>

        <!-- Filters -->
        <b-card class="mb-4">
          <b-row>
            <b-col :cols="12" :md="3">
              <b-form-group label="Đối tác (Partner ID)">
                <b-form-input v-model="filter.partnerId" placeholder="Để trống = tất cả" @keyup.enter="applyFilter" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="3">
              <b-form-group label="Loại dịch vụ">
                <b-form-select v-model="filter.service" :options="serviceOptions" @change="applyFilter" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="3">
              <b-form-group label="Loại sự kiện">
                <b-form-select v-model="filter.eventType" :options="eventTypeOptions" @change="applyFilter" />
              </b-form-group>
            </b-col>
            <b-col :cols="12" :md="3">
              <b-form-group label="Khoảng thời gian">
                <date-picker
                  v-model="filter.range"
                  :range="true"
                  input-class="form-control"
                  type="date"
                  format="DD/MM/YYYY"
                  value-type="date"
                  style="width: 100%"
                  @change="applyFilter"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Error state -->
        <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
          {{ error }}
        </b-alert>

        <!-- Summary cards -->
        <div class="sdk-stats-totals mb-4">
          <div class="sdk-stats-tile">
            <div class="sdk-stats-tile-label">Sự kiện</div>
            <div class="sdk-stats-tile-value">{{ (totals.events || 0).toLocaleString('vi-VN') }}</div>
          </div>
          <div class="sdk-stats-tile">
            <div class="sdk-stats-tile-label">Khách hàng</div>
            <div class="sdk-stats-tile-value">{{ (totals.customers || 0).toLocaleString('vi-VN') }}</div>
          </div>
          <div class="sdk-stats-tile is-booked">
            <div class="sdk-stats-tile-label">Đặt lịch</div>
            <div class="sdk-stats-tile-value">{{ (totals.booked || 0).toLocaleString('vi-VN') }}</div>
          </div>
          <div class="sdk-stats-tile is-cancelled">
            <div class="sdk-stats-tile-label">Huỷ</div>
            <div class="sdk-stats-tile-value">{{ (totals.cancelled || 0).toLocaleString('vi-VN') }}</div>
          </div>
          <div class="sdk-stats-tile is-blocked">
            <div class="sdk-stats-tile-label">Bị chặn</div>
            <div class="sdk-stats-tile-value">{{ (totals.blocked || 0).toLocaleString('vi-VN') }}</div>
          </div>
          <div class="sdk-stats-tile">
            <div class="sdk-stats-tile-label">Đăng nhập</div>
            <div class="sdk-stats-tile-value">{{ (totals.logins || 0).toLocaleString('vi-VN') }}</div>
          </div>
        </div>

        <!-- Dimension tabs -->
        <b-tabs v-model="activeTab" @input="onTabChange">
          <b-tab title="Theo đối tác" />
          <b-tab title="Theo dịch vụ" />
          <b-tab title="Theo ngày" />
          <b-tab title="Theo sự kiện" />
          <b-tab v-if="canViewCustomer" title="Theo khách hàng" />
        </b-tabs>

        <!-- Loading skeleton -->
        <div v-if="loading" class="sdk-stats-loading">
          <b-spinner variant="primary" class="mr-2" />Đang tải dữ liệu...
        </div>

        <!-- Table -->
        <div v-else class="table-responsive mt-3">
          <table v-if="rows.length" class="table table-sm table-hover">
            <thead>
              <tr>
                <th>{{ dimensionLabel }}</th>
                <th class="text-right">Số lượng</th>
                <th>
                  <!-- Bar chart column (no extra lib needed) -->
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key">
                <td>{{ row.key || '(trống)' }}</td>
                <td class="text-right font-weight-bold">{{ (row.count || 0).toLocaleString('vi-VN') }}</td>
                <td class="sdk-bar-cell">
                  <div class="sdk-bar-track">
                    <div class="sdk-bar-fill" :style="{ width: barPct(row.count) + '%' }" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="sdk-empty">
            <i class="fas fa-chart-bar" />
            <span>Không có dữ liệu cho bộ lọc này</span>
          </div>
        </div>
      </template>
    </Wrapper>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import SdkPartnerService, { SERVICE_TYPES, EVENT_TYPES } from './services/sdk-partner.service';

const DIMS = ['partner', 'service', 'day', 'event', 'customer'];

export default {
  name: 'sdk_partner_stats',
  components: { 'date-picker': DatePicker },
  data() {
    return {
      isConfigured: SdkPartnerService.isConfigured(),
      needLogin: !SdkPartnerService.hasToken(),
      loginLoading: false,
      loginForm: { email: '', password: '' },
      loading: false,
      error: null,
      activeTab: 0,
      rows: [],
      totals: {},
      filter: {
        partnerId: '',
        service: null,
        eventType: null,
        range: null,
      },
      serviceOptions: [
        { value: null, text: 'Tất cả dịch vụ' },
        ...SERVICE_TYPES.map((s) => ({ value: s, text: s })),
      ],
      canViewCustomer: false,
      eventTypeOptions: [
        { value: null, text: 'Tất cả sự kiện' },
        ...EVENT_TYPES.map((e) => ({ value: e, text: e })),
      ],
    };
  },
  computed: {
    currentDim() {
      return DIMS[this.activeTab] || 'partner';
    },
    dimensionLabel() {
      const map = {
        partner: 'Đối tác',
        service: 'Dịch vụ',
        day: 'Ngày',
        event: 'Sự kiện',
        customer: 'SĐT khách hàng',
      };
      return map[this.currentDim] || 'Dimension';
    },
    rowMax() {
      return this.rows.reduce((m, r) => Math.max(m, r.count || 0), 0);
    },
  },
  created() {
    if (this.isConfigured && !this.needLogin) {
      this.loadStats();
      SdkPartnerService.ensureCanViewRequestLogs().then((v) => { this.canViewCustomer = v; });
    }
  },
  methods: {
    barPct(count) {
      if (!count || !this.rowMax) return 0;
      return Math.max(2, Math.round((count / this.rowMax) * 100));
    },
    buildParams() {
      const params = { by: this.currentDim };
      if (this.filter.partnerId) params.partnerId = this.filter.partnerId.trim();
      if (this.filter.service) params.service = this.filter.service;
      if (this.filter.eventType) params.eventType = this.filter.eventType;
      if (this.filter.range && this.filter.range[0]) {
        params.from = this.$moment(this.filter.range[0]).format('YYYY-MM-DD');
      }
      if (this.filter.range && this.filter.range[1]) {
        params.to = this.$moment(this.filter.range[1]).format('YYYY-MM-DD');
      }
      return params;
    },
    async loadStats() {
      this.loading = true;
      this.error = null;
      try {
        const params = this.buildParams();
        const res = await SdkPartnerService.getStats(params);
        const data = (res && res.data) ? res.data : res;
        this.rows = (data && data.rows) || [];
        this.totals = (data && data.totals) || {};
      } catch (err) {
        if (err && err.message === 'SDK_ADMIN_NO_TOKEN') {
          this.needLogin = true;
          return;
        }
        const status = err && err.code;
        if (status === 403) {
          this.error = 'Bạn chưa được cấp quyền xem dữ liệu này (RequestLogAccessDenied).';
        } else {
          this.error = (err && err.message) || 'Đã có lỗi khi tải thống kê';
        }
      } finally {
        this.loading = false;
      }
    },
    applyFilter() {
      this.loadStats();
    },
    onTabChange() {
      this.loadStats();
    },
    async doLogin() {
      this.loginLoading = true;
      try {
        await SdkPartnerService.login(this.loginForm.email, this.loginForm.password);
        this.needLogin = false;
        this.loadStats();
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi đăng nhập',
          msg: (err && err.message) || 'Đăng nhập thất bại',
          preventDuplicates: true,
        });
      } finally {
        this.loginLoading = false;
      }
    },
    exportCsv() {
      if (!this.rows.length) return;
      // UTF-8 BOM để Excel nhận dạng đúng tiếng Việt
      const BOM = '﻿';
      const header = [this.dimensionLabel, 'Số lượng'];
      const escape = (v) => {
        const s = String(v == null ? '' : v);
        // Escape: nếu có dấu phẩy, ngoặc kép hoặc xuống dòng thì bọc trong ""
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };
      const lines = [header.map(escape).join(',')];
      this.rows.forEach((r) => {
        lines.push([escape(r.key || ''), escape(r.count || 0)].join(','));
      });
      const csv = BOM + lines.join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sdk-stats-${this.currentDim}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.sdk-stats-totals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.sdk-stats-tile {
  padding: 16px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #fff;
  &.is-booked { border-left: 4px solid #0bb783; }
  &.is-cancelled { border-left: 4px solid #f64e60; }
  &.is-blocked { border-left: 4px solid #ffa800; }
}
.sdk-stats-tile-label {
  font-size: 12px;
  color: #7e8299;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.sdk-stats-tile-value {
  font-size: 24px;
  font-weight: 700;
  margin-top: 4px;
}

.sdk-bar-cell {
  width: 200px;
  min-width: 100px;
}
.sdk-bar-track {
  height: 8px;
  background: #ebedf3;
  border-radius: 4px;
  overflow: hidden;
}
.sdk-bar-fill {
  height: 100%;
  background: #8950fc;
  border-radius: 4px;
  transition: width 0.2s;
}

.sdk-stats-loading {
  display: flex;
  align-items: center;
  padding: 24px 0;
  color: #7e8299;
}

.sdk-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 96px;
  padding: 22px;
  color: #a1a5b7;
  border: 1px dashed #d7dae3;
  border-radius: 10px;
  background: #fafbfd;
  font-size: 13px;
  i { font-size: 22px; opacity: 0.6; }
}

.sdk-login-panel {
  max-width: 780px;
  padding: 32px;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
}
</style>
